import AboutLink from '../Auth/AboutLink';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Avatar from '@mui/joy/Avatar';

import Diversity1OutlinedIcon from '@mui/icons-material/Diversity1Outlined';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import { closeSidebar, NAME_PROJECT } from '../../utils';
import { useLocation, useNavigate } from 'react-router';
import { useContext } from 'react';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { LayoutContext } from '../../context';

const NAV = [
    { path: '/match', label: 'Симпатии', Icon: FavoriteBorderRoundedIcon },
    { path: '/lenta', label: 'Лента', Icon: StyleOutlinedIcon },
    { path: '/quizzes', label: 'Тесты', Icon: QuizOutlinedIcon },
    { path: '/chats', label: 'Чаты', Icon: ChatBubbleOutlineRoundedIcon },
    { path: '/unions', label: 'Союзы', Icon: Diversity1OutlinedIcon },
];

/**
 * Боковое меню (ПК и планшет; на телефоне выезжает поверх).
 *
 * Было: плоский список с серой рамкой справа и логотипом по относительному
 * пути './logo.png' — на вложенных маршрутах он давал 404.
 * Стало: стеклянная панель, мягкая активная плашка с цветной контурной иконкой,
 * внизу карточка своей анкеты.
 */
export default function Sidebar() {
    const { profileUser, setModalQuestionnaire } = useContext(LayoutContext);

    const reduceMotion = useReducedMotion();
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

    const go = (path) => {
        navigate(path, { replace: true });
        closeSidebar();
    };

    return (
        <Sheet
            className="Sidebar lvc-scroll"
            sx={{
                position: { xs: 'fixed', md: 'sticky' },
                transform: {
                    xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
                    md: 'none',
                },
                transition: reduceMotion ? 'none' : 'transform 0.3s ease, width 0.3s ease',
                zIndex: 10000,
                height: { xs: '100dvh', md: '100%' },
                width: 'var(--Sidebar-width)',
                top: 0,
                p: 2,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                backgroundColor: 'rgba(255,255,255,0.72)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                borderRight: '1px solid',
                borderColor: 'var(--lvc-line)',
                overflowY: 'auto',
            }}
        >
            <GlobalStyles
                styles={(theme) => ({
                    ':root': {
                        '--Sidebar-width': '232px',

                    },
                })}
            />

            <Box
                className="Sidebar-overlay"
                sx={{
                    display: { xs: 'block', md: 'none' },
                    position: 'fixed',
                    zIndex: 9998,
                    top: 0,
                    left: '32px',
                    width: '100vw',
                    height: '100vh',
                    opacity: 'var(--SideNavigation-slideIn)',
                    backgroundColor: 'rgba(43, 29, 58, 0.45)',
                    transition: reduceMotion ? 'none' : 'opacity 0.3s',
                    transform: {
                        xs: 'translateX(calc(120% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
                        lg: 'translateX(-120%)',
                    },
                }}
                onClick={() => closeSidebar()}
            />

            {/* Бренд */}
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', px: 0.5, pt: 0.5 }}>
                <img
                    src="/logo.png"
                    alt=""
                    style={{ width: 40, height: 40, objectFit: 'contain', display: 'block' }}
                />
                <Typography
                    level="title-lg"
                    className="lvc-grad-text"
                    sx={{ lineHeight: 1.1, fontWeight: 800, letterSpacing: '-0.01em' }}
                >
                    {NAME_PROJECT}
                </Typography>
            </Box>

            {/* Мягкая активная плашка и единые контурные иконки. */}
            <Box sx={{ minHeight: 0, flexGrow: 1, pt: 1 }}>
                <LayoutGroup id="lovirds-sidebar-navigation">
                    <Box component="nav" aria-label="Разделы" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {NAV.map(({ path, label, Icon }) => {
                            const active = isActive(path);
                            return (
                                <Box
                                    key={path}
                                    component={motion.button}
                                    type="button"
                                    aria-current={active ? 'page' : undefined}
                                    onClick={() => go(path)}
                                    whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                                    sx={{
                                        position: 'relative', isolation: 'isolate', display: 'flex',
                                        alignItems: 'center', gap: 1.25, width: '100%', minHeight: 52,
                                        py: 0.75, px: 1.25, border: 0, borderRadius: '17px',
                                        background: 'transparent', textAlign: 'left', font: 'inherit',
                                        fontSize: 13, fontWeight: active ? 700 : 500,
                                        color: active ? '#7040a8' : '#806c8d', cursor: 'pointer',
                                        transition: reduceMotion ? 'none' : 'color .18s, background .18s',
                                        '&:hover': { backgroundColor: active ? 'transparent' : 'rgba(244,236,251,.6)', color: '#7544b0' },
                                        '&:focus-visible': { outline: '2px solid #a87bdd', outlineOffset: '2px' },
                                    }}
                                >
                                    {active && (
                                        <Box
                                            component={motion.span}
                                            layoutId={reduceMotion ? undefined : 'sidebar-active-surface'}
                                            transition={{ type: 'spring', stiffness: 420, damping: 36, duration: reduceMotion ? 0 : undefined }}
                                            sx={{
                                                position: 'absolute', inset: 0, zIndex: -1, borderRadius: '17px',
                                                background: 'linear-gradient(110deg, rgba(240,226,253,.88), rgba(244,239,255,.88))',
                                                border: '1px solid rgba(161,117,214,.12)',
                                            }}
                                        />
                                    )}
                                    <Box component="span" sx={{
                                        display: 'grid', placeItems: 'center', flex: 'none',
                                        width: 34, height: 34, borderRadius: '12px',
                                        backgroundColor: active ? 'rgba(255,255,255,.84)' : 'transparent',
                                        color: active ? '#9454d8' : '#9b83a8',
                                        boxShadow: active ? '0 3px 10px rgba(131,76,176,.06)' : 'none',
                                        transition: reduceMotion ? 'none' : 'background .18s, color .18s',
                                    }}>
                                        <Icon sx={{ fontSize: 21, color: active ? '#9454d8' : '#9b83a8' }} />
                                    </Box>
                                    <Box component="span" sx={{ minWidth: 0, flex: 1 }}>{label}</Box>
                                    {active && <Box component="span" aria-hidden="true" sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#b58adb', mr: 0.5 }} />}
                                </Box>
                            );
                        })}
                    </Box>
                </LayoutGroup>
            </Box>

            <Box sx={{
                '& .lv-about-link': {
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    width: '100%', minHeight: 36, py: 0.5, px: 1.5,
                    boxSizing: 'border-box', borderRadius: '12px',
                    background: 'rgba(138, 77, 255, 0.06)',
                    border: '1px solid var(--lvc-line)',
                    color: 'var(--lvc-ink-soft)', fontSize: 13,
                    transition: 'background .2s, color .2s',
                    '&:hover': { background: 'rgba(138, 77, 255, 0.11)', color: '#7a3ae8' },
                    '&:focus-visible': { outline: '2px solid #9b4cf1', outlineOffset: '3px' },
                },
            }}>
                <AboutLink onClick={closeSidebar} />
            </Box>

            {/* Своя анкета */}
            <Box
                component="button"
                type="button"
                onClick={() => {
                    setModalQuestionnaire(prev => ({ ...prev, open: true }));
                    closeSidebar();
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    width: '100%',
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'var(--lvc-line)',
                    borderRadius: '18px',
                    backgroundColor: '#fff',
                    boxShadow: 'var(--lvc-shadow)',
                    cursor: 'pointer',
                    font: 'inherit',
                    textAlign: 'left',
                    transition: 'transform .2s, box-shadow .2s',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 'var(--lvc-shadow-lg)' },
                }}
            >
                {profileUser?.photo_file_path ? (
                    <Avatar
                        src={profileUser.photo_file_path}
                        alt={profileUser?.name}
                        sx={{ '--Avatar-size': '38px', borderRadius: '14px', flex: 'none' }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: 38, height: 38, borderRadius: '14px', flex: 'none',
                            display: 'grid', placeItems: 'center',
                            background: 'var(--lvc-grad-soft)', color: '#a855f7',
                        }}
                    >
                        <PersonOutlineRoundedIcon sx={{ color: '#9673b8' }} />
                    </Box>
                )}

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography level="title-sm" noWrap sx={{ fontWeight: 700 }}>
                        {profileUser?.name || 'Моя анкета'}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'var(--lvc-ink-soft)' }}>
                        Моя анкета
                    </Typography>
                </Box>

                <ChevronRightRoundedIcon sx={{ color: 'var(--lvc-ink-soft)', flex: 'none' }} />
            </Box>
        </Sheet>
    );
}
